import { Injectable } from '@angular/core';
import { FormActionHandlerService } from './base.service';
import { FormStatusService } from '../status.service';
import { SwalService } from '../../../fastlane-common/services/swal.service';
import { FormDataService } from '../data.service';
import { FormShareService } from '../share.service';
import { UserContextService } from '../../../user/services/user-context.service';
import { UserDataService } from '../../../user/services/user-data.service';
import { EmailService } from '../../../email/services/email.service';
import { permissions } from '../../../security/security.constants';
import { FormStatus, formStatus } from 'app/form/form.constants';
import { SweetAlertOptions } from 'sweetalert2';


export abstract class RdReviewStageActionHandlerService extends FormActionHandlerService {
  protected abstract targetStatus: FormStatus;

  protected nextReviewerPermission?: string;
  protected nextReviewerRoleName?: string;

  constructor(
    protected dataService: FormDataService,
    protected statusService: FormStatusService,
    protected swal: SwalService,
    protected shareService: FormShareService,
    protected userContext: UserContextService,
    protected emailService: EmailService,
    protected userService: UserDataService
  ) {
    super();
  }

  exec(): Promise<any> {
    const that = this;
    return new Promise((resolve, reject) => {
      that.swal.load('Changing Status to ' + that.targetStatus);
      that.statusService.changeStatus(that.targetStatus);

      that.dataService.saveForm().subscribe(
        () => {
          // Notify the next party in the reviewer chain. Best-effort: a failed
          // (or unassigned) notification never undoes the saved status change.
          that.notifyNextParty().then(() => {
            that.showResultDialog().then(resolve).catch(resolve);
          });
        },
        errorSaving => {
          if (errorSaving === 409) {
            reject(errorSaving);
          } else {
            reject(
              'We were unable to update the form status. Please try again later.'
            );
          }
        }
      );
    });
  }

  private showResultDialog(): Promise<any> {
    const that = this;
    const successText = 'Form status set to ' + that.targetStatus + '.';

    if (!that.nextReviewerPermission) {
      return that.swal.success({ title: 'Success', text: successText });
    }

    return new Promise(resolve => {
      that.userService
        .getManagersWithFilters('', that.nextReviewerPermission, '', '')
        .subscribe(
          users => {
            if ((users || []).length > 0) {
              that.swal
                .success({ title: 'Success', text: successText })
                .then(resolve)
                .catch(resolve);
            } else {
              that.swal
                .warn({
                  title: 'No ' + that.nextReviewerRoleName + ' Assigned',
                  text:
                    successText +
                    ' However, no user is currently set up with the ' +
                    that.nextReviewerRoleName +
                    ' role to receive the form next. Please assign one under ' +
                    'Manage General Users.'
                })
                .then(resolve)
                .catch(resolve);
            }
          },
          () =>
            that.swal
              .success({ title: 'Success', text: successText })
              .then(resolve)
              .catch(resolve)
        );
    });
  }

  private notifyNextParty(): Promise<void> {
    const that = this;
    return new Promise(resolve => {
      const projectInfo = that.shareService.projectBase.projectInfo;
      const form = that.shareService.form;
      const projectGuid = that.shareService.formId.projectGuid;
      const currentUser = that.userContext.currentUser;
      const completedBy = currentUser
        ? `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
        : '';

      that.emailService
        .sendRdReviewStageNotification(
          projectInfo,
          form,
          projectGuid,
          completedBy,
          that.targetStatus
        )
        .subscribe(() => resolve(), () => resolve());
    });
  }
}

@Injectable()
export class ManagerReviewCompleteActionHandlerService extends RdReviewStageActionHandlerService {
  protected targetStatus = formStatus.managerReviewComplete;
  protected nextReviewerPermission = permissions.rdAssistantDirector.value;
  protected nextReviewerRoleName = 'Assistant Director';

  constructor(
    dataService: FormDataService,
    statusService: FormStatusService,
    swal: SwalService,
    shareService: FormShareService,
    userContext: UserContextService,
    emailService: EmailService,
    userService: UserDataService
  ) {
    super(dataService, statusService, swal, shareService, userContext, emailService, userService);
  }

  exec(): Promise<any> {
    const projectInfo = this.shareService.project.projectInfo;
    const swalOptions = <SweetAlertOptions>{
      title: 'Please assign an RD ID',
      type: 'success',
      inputValue: projectInfo['rdId'] || '',
      inputPlaceholder: 'Please enter the RD ID',
      confirmButtonText: 'Assign RD ID'
    };
    return this.swal.textarea(swalOptions, 'required', 'The RD ID is required.').then(
      rdId => {
        projectInfo['rdId'] = (rdId || '').toString().trim();
        return super.exec();
      },
      () => Promise.resolve()
    );
  }
}

@Injectable()
export class AssistantDirectorReviewCompleteActionHandlerService extends RdReviewStageActionHandlerService {
  protected targetStatus = formStatus.assistantDirectorReviewComplete;
  protected nextReviewerPermission = permissions.rdDirector.value;
  protected nextReviewerRoleName = 'Director';

  constructor(
    dataService: FormDataService,
    statusService: FormStatusService,
    swal: SwalService,
    shareService: FormShareService,
    userContext: UserContextService,
    emailService: EmailService,
    userService: UserDataService
  ) {
    super(dataService, statusService, swal, shareService, userContext, emailService, userService);
  }
}

@Injectable()
export class DirectorReviewCompleteActionHandlerService extends RdReviewStageActionHandlerService {
  protected targetStatus = formStatus.directorReviewComplete;

  constructor(
    dataService: FormDataService,
    statusService: FormStatusService,
    swal: SwalService,
    shareService: FormShareService,
    userContext: UserContextService,
    emailService: EmailService,
    userService: UserDataService
  ) {
    super(dataService, statusService, swal, shareService, userContext, emailService, userService);
  }
}
